import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../../utils/apiClient';
import {
    UploadCloud, FileCode, Play, Loader2, CheckCircle, X, RefreshCw, FileText, AlertCircle,
    ChevronDown, ChevronRight, Filter, Search, ArrowUp, ArrowDown, ArrowUpDown, Calendar
} from 'lucide-react';
import { BacktestView } from '../../components/BacktestView';
import DashboardLayout from "../DashboardLayout";
import server from '../../utils/serverConfig';

// --- Audit Workflow Component ---
const AuditWorkflow = () => {
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState('IDLE'); // IDLE, UPLOADING, PROCESSING, DONE, FAILED
    const [progress, setProgress] = useState(0);
    const [message, setMessage] = useState('');
    const [jobId, setJobId] = useState(null);
    const [auditHistory, setAuditHistory] = useState([]);
    const [error, setError] = useState(null);
    const [isFullScreen, setIsFullScreen] = useState(false);

    // New Features State
    const [filters, setFilters] = useState({});
    const [collapsed, setCollapsed] = useState({ live: false, backtest: false });
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [activeFilterColumn, setActiveFilterColumn] = useState(null);

    // Date Filter State
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const fileInputRef = useRef(null);
    const filterMenuRef = useRef(null);
    const API_URL = `https://backtest.sypto.xyz/`; // Adjust if needed

    // Fetch Audit History
    const fetchHistory = async () => {
        if (!fromDate || !toDate) {
            // Optional: User requested "initial only when date range put"
            // So we don't fetch if empty.
            // console.log("Skipping fetch: No dates selected");
            return;
        }

        try {
            const url = `${server.godseye.baseUrl}audit-results?start_date=${fromDate}&end_date=${toDate}&limit=1000`;
            const res = await apiClient.get(url);
            setAuditHistory(res.data);
        } catch (e) {
            console.error("Failed to fetch audit history", e);
            setError("Failed to fetch results. Check backend connection.");
        }
    };

    // Auto-fetch ONLY if we have dates (e.g. after upload completes? or just manual)
    // User said: "fetch initially only when we put date range start and end"
    useEffect(() => {
        if (fromDate && toDate) {
            fetchHistory();
        }
    }, [fromDate, toDate]);

    // Polling Logic
    useEffect(() => {
        let interval;
        let isActive = true;

        if ((status === 'PROCESSING' || status === 'UPLOADING') && jobId) {
            interval = setInterval(async () => {
                try {
                    const res = await apiClient.get(`${API_URL}job/${jobId}`);
                    if (!isActive) return;

                    const data = res.data;
                    setProgress(data.progress);
                    setMessage(data.message);

                    if (data.status === 'COMPLETED') {
                        setStatus('DONE');
                        setProgress(100);
                        // Optional: If job completed, maybe we want to fetch the results?
                        // If so, we need to know the date range of that job.
                        // For now, let's leave it manual or keep previous results if any.
                        if (fromDate && toDate) fetchHistory();
                    } else if (data.status === 'FAILED') {
                        setStatus('FAILED');
                        setError(data.message);
                    }
                } catch (e) {
                    console.error("Poll error", e);
                }
            }, 2000);
        }
        return () => {
            isActive = false;
            if (interval) clearInterval(interval);
        };
    }, [status, jobId, fromDate, toDate]);

    // Close Dropdown on Click Outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
                setActiveFilterColumn(null);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleFileSelect = (e) => {
        if (e.target.files?.[0]) {
            setFile(e.target.files[0]);
            setError(null);
        }
    };

    const handleUpload = async () => {
        if (!file) return;
        try {
            setStatus('UPLOADING');
            const formData = new FormData();
            formData.append('file', file);

            const res = await apiClient.post(`${API_URL}upload-result`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const data = res.data;
            setJobId(data.job_id);
            setStatus('PROCESSING');
            setMessage('Upload successful. Processing started...');
        } catch (e) {
            console.error(e);
            setStatus('FAILED');
            setError(e.response?.data?.detail || "Upload failed");
        }
    };

    const reset = () => {
        setFile(null);
        setStatus('IDLE');
        setProgress(0);
        setMessage('');
        setJobId(null);
        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const formatDateTime = (val) => {
        if (!val) return '-';
        return new Date(val).toLocaleString([], {
            year: 'numeric', month: 'numeric', day: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
        });
    };

    // Sorting Handler
    const handleSort = (key, direction) => {
        // Toggle off if clicking the same sort already active
        if (sortConfig.key === key && sortConfig.direction === direction) {
            setSortConfig({ key: null, direction: 'asc' });
        } else {
            setSortConfig({ key, direction });
        }
    };

    // Filter Handler
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Process Data: Filter -> Sort
    const processedData = React.useMemo(() => {
        let data = [...auditHistory];

        // 1. Filter
        data = data.filter(row => {
            return Object.entries(filters).every(([key, filterVal]) => {
                if (!filterVal) return true;
                const rowVal = row[key];
                if (rowVal === null || rowVal === undefined) return false;
                // Special Exact Match for Status
                if (key === 'status') {
                    return String(rowVal) === filterVal;
                }
                return String(rowVal).toLowerCase().includes(filterVal.toLowerCase());
            });
        });

        // 2. Sort
        if (sortConfig.key) {
            data.sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];

                if (aVal === bVal) return 0;

                // Handle nulls
                if (aVal === null) return 1;
                if (bVal === null) return -1;

                // Detect type (Date, Number, String)
                const isNum = !isNaN(parseFloat(aVal)) && isFinite(aVal) && !isNaN(parseFloat(bVal)) && isFinite(bVal);
                if (isNum) {
                    return sortConfig.direction === 'asc'
                        ? Number(aVal) - Number(bVal)
                        : Number(bVal) - Number(aVal);
                }

                if (sortConfig.direction === 'asc') {
                    return aVal > bVal ? 1 : -1;
                } else {
                    return aVal < bVal ? 1 : -1;
                }
            });
        }
        return data;
    }, [auditHistory, filters, sortConfig]);

    // Render Column Helper with Dropdown
    const renderHeaderCell = (label, filterKey, type = "text", className = "") => {
        const isActive = activeFilterColumn === filterKey;
        const isFiltered = filters[filterKey] && filters[filterKey].length > 0;
        const isSorted = sortConfig.key === filterKey;

        return (
            <th className={`px-4 py-3 shadow-[inset_0_-1px_0_0_#1F2937]
 border-gray-800 relative ${className}`}>
                <div className="flex items-center justify-between gap-2 group">
                    <span className={`text-xs uppercase font-bold ${isSorted ? "text-blue-400" : ""}`}>
                        {label} {isSorted && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </span>

                    <button
                        onClick={(e) => { e.stopPropagation(); setActiveFilterColumn(isActive ? null : filterKey); }}
                        className={`p-1 rounded hover:bg-gray-700 transition-colors ${isFiltered || isActive ? 'text-blue-400 bg-gray-800' : 'text-gray-500 opacity-0 group-hover:opacity-100'}`}
                    >
                        <Filter size={14} />
                    </button>

                    {/* Dropdown Menu */}
                    {isActive && (
                        <div
                            ref={filterMenuRef}
                            className="absolute top-full right-0 mt-2 w-56 bg-[#1a1d29] border border-gray-700 rounded-lg shadow-xl z-50 p-3 flex flex-col gap-3 animation-fade-in"
                            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
                        >
                            <div className="flex flex-col gap-1 border-b border-gray-800 pb-2">
                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-1">Sort</span>

                                <button
                                    onClick={() => handleSort(filterKey, 'desc')}
                                    className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left transition-colors ${sortConfig.key === filterKey && sortConfig.direction === 'desc' ? 'bg-blue-500/20 text-blue-400' : 'hover:bg-gray-800 text-gray-300'}`}
                                >
                                    {type === 'number' || type === 'price' ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
                                    <span>{type === 'time' ? 'Newest First' : type === 'price' ? 'Highest First' : 'Desc (Z-A)'}</span>
                                </button>

                                <button
                                    onClick={() => handleSort(filterKey, 'asc')}
                                    className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left transition-colors ${sortConfig.key === filterKey && sortConfig.direction === 'asc' ? 'bg-blue-500/20 text-blue-400' : 'hover:bg-gray-800 text-gray-300'}`}
                                >
                                    {type === 'number' || type === 'price' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                                    <span>{type === 'time' ? 'Oldest First' : type === 'price' ? 'Lowest First' : 'Asc (A-Z)'}</span>
                                </button>
                            </div>

                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-1">Filter</span>

                                {type === 'status' ? (
                                    <div className="flex flex-col gap-2">
                                        {['Match', 'Mismatch'].map(option => (
                                            <button
                                                key={option}
                                                onClick={() => handleFilterChange(filterKey, filters[filterKey] === option ? '' : option)}
                                                className={`flex items-center justify-between px-2 py-1.5 rounded text-xs transition-colors border
                                                    ${filters[filterKey] === option
                                                        ? (option === 'Match' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-red-500/10 text-red-400 border-red-500/30')
                                                        : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:bg-gray-800'}`}
                                            >
                                                <span>{option}</span>
                                                {filters[filterKey] === option && <CheckCircle size={12} />}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Search size={12} className="absolute left-2 top-2 text-gray-500" />
                                        <input
                                            type="text"
                                            placeholder={`Search ${label}...`}
                                            className="bg-[#0f1116] border border-gray-700 rounded w-full pl-7 pr-7 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-blue-500 transition-colors"
                                            value={filters[filterKey] || ''}
                                            onChange={(e) => handleFilterChange(filterKey, e.target.value)}
                                            autoFocus
                                        />
                                        {filters[filterKey] && (
                                            <button
                                                onClick={() => handleFilterChange(filterKey, '')}
                                                className="absolute right-2 top-2 text-gray-500 hover:text-white"
                                            >
                                                <X size={12} />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </th>
        );
    };

    return (
        <div className="flex flex-col h-full gap-6 overflow-hidden relative">

            {/* Header & Controls */}
            <div className="flex flex-col xl:flex-row gap-4 justify-between items-start xl:items-center bg-[#1a1d29] p-4 rounded-xl shadow-lg border border-gray-800 shrink-0">
                <div>
                    <h1 className="text-2xl font-bold text-gray-100 flex items-center gap-2">
                        <FileText className="w-6 h-6 text-blue-500" />
                        Audit Results
                    </h1>
                    <p className="text-gray-400 text-sm mt-1">Upload backtest ZIP to compare against live trades</p>
                </div>

                <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
                    {/* Date Filters */}
                    <div className="flex items-center gap-2 bg-[#14161f] p-1.5 rounded-lg border border-gray-800 shadow-sm">
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider px-2">Range</span>

                        <div className="relative">
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                style={{ colorScheme: "dark" }}
                                className="px-3 py-1.5 bg-[#1a1d29] border border-gray-700 rounded-md text-xs font-semibold text-gray-300 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all hover:border-gray-600 cursor-pointer"
                            />
                        </div>

                        <span className="text-gray-600 font-light px-1">to</span>

                        <div className="relative">
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                style={{ colorScheme: "dark" }}
                                className="px-3 py-1.5 bg-[#1a1d29] border border-gray-700 rounded-md text-xs font-semibold text-gray-300 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all hover:border-gray-600 cursor-pointer"
                            />
                        </div>

                        <button
                            onClick={fetchHistory}
                            disabled={!fromDate || !toDate}
                            className="ml-2 p-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all active:scale-95 border border-blue-500/50"
                            title="Fetch Results"
                        >
                            <Search className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    <div className="h-8 w-[1px] bg-gray-800 mx-2 hidden xl:block"></div>

                    {/* File Upload Inputs */}
                    <div className="flex items-center gap-3">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            className="hidden"
                            accept=".zip"
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="px-4 py-2 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 flex items-center gap-2 transition-colors text-sm font-medium hover:border-gray-600"
                        >
                            <FileCode className="w-4 h-4 text-gray-400" />
                            {file ? file.name : 'Select ZIP'}
                        </button>

                        <button
                            onClick={handleUpload}
                            disabled={!file || status === 'UPLOADING' || status === 'PROCESSING'}
                            className={`px-4 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-2 shadow-sm transition-all ${!file || status === 'UPLOADING' || status === 'PROCESSING'
                                ? 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700'
                                : 'bg-blue-600 hover:bg-blue-700 hover:shadow-md border border-blue-500/50'
                                }`}
                        >
                            {status === 'UPLOADING' ? <Loader2 className="w-4 h-4 animate-spin" /> :
                                status === 'PROCESSING' ? <RefreshCw className="w-4 h-4 animate-spin" /> :
                                    <Play className="w-4 h-4" />}
                            {status === 'UPLOADING' ? 'Uploading...' :
                                status === 'PROCESSING' ? 'Running...' : 'Run Audit'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Processing State Indicator */}
            {status !== 'IDLE' && status !== 'DONE' && status !== 'FAILED' && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-center gap-3 text-sm text-blue-700 animate-pulse shrink-0">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{message || "Processing..."} ({progress}%)</span>
                    {/* Add Cancel Button if needed */}
                </div>
            )}

            {status === 'FAILED' && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center justify-between gap-3 text-sm text-red-400 shrink-0">
                    <div className="flex items-center gap-3">
                        <AlertCircle className="w-4 h-4" />
                        <span>{error || message || "Audit job failed"}</span>
                    </div>
                    <button
                        onClick={reset}
                        className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 rounded text-xs font-semibold uppercase tracking-wide transition-colors"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {status === 'DONE' && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-center gap-3 text-sm text-emerald-400 shrink-0">
                    <CheckCircle className="w-4 h-4" />
                    <span>{message || "Backtest completed successfully!"}</span>
                </div>
            )}



            {/* Analytics Section */}
            {processedData.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 shrink-0">
                    {/* 1. Total Overview */}
                    <div className="bg-[#1a1d29] border border-gray-800 p-4 rounded-xl shadow-lg">
                        <div className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2">Overview</div>
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-2xl font-bold text-emerald-400">
                                    {processedData.filter(d => d.status === 'Match').length}
                                </div>
                                <div className="text-[10px] text-emerald-500/70 font-medium">Matches</div>
                            </div>
                            <div className="h-8 w-[1px] bg-gray-700"></div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-red-400">
                                    {processedData.filter(d => d.status !== 'Match').length}
                                </div>
                                <div className="text-[10px] text-red-500/70 font-medium">Mismatches</div>
                            </div>
                        </div>
                    </div>

                    {/* Helper to calculate aggregations */}
                    {(() => {
                        const dateMap = {};
                        const stratMap = {};

                        processedData.forEach(row => {
                            // Extract Date
                            const time = row.buy_time_l || row.buy_time_b || row.sell_time_l || row.sell_time_b;
                            const date = time ? new Date(time).toLocaleDateString() : 'Unknown';

                            // Extract Strategy
                            const strat = row.strategy_tag_l || row.strategy_tag_b || 'Unknown';

                            // Init maps
                            if (!dateMap[date]) dateMap[date] = { match: 0, mismatch: 0 };
                            if (!stratMap[strat]) stratMap[strat] = { match: 0, mismatch: 0 };

                            if (row.status === 'Match') {
                                dateMap[date].match++;
                                stratMap[strat].match++;
                            } else {
                                dateMap[date].mismatch++;
                                stratMap[strat].mismatch++;
                            }
                        });

                        // Find Maxes
                        const findMax = (map, key) => {
                            let maxKey = '-';
                            let maxVal = -1;
                            Object.entries(map).forEach(([k, v]) => {
                                if (v[key] > maxVal) {
                                    maxVal = v[key];
                                    maxKey = k;
                                }
                            });
                            return { key: maxKey, val: maxVal };
                        };

                        const worstDate = findMax(dateMap, 'mismatch');
                        const bestDate = findMax(dateMap, 'match');
                        const worstStrat = findMax(stratMap, 'mismatch');
                        const bestStrat = findMax(stratMap, 'match');

                        return (
                            <>
                                {/* 2. Worst Date (High Mismatch) */}
                                <div className="bg-[#1a1d29] border border-gray-800 p-4 rounded-xl shadow-lg relative group overflow-hidden">
                                    <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                        <Calendar className="w-12 h-12 text-red-500" />
                                    </div>
                                    <div className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3 text-red-500" /> High Mismatch Date
                                    </div>
                                    <div className="text-xl font-bold text-white truncate" title={worstDate.key}>{worstDate.key}</div>
                                    <div className="text-xs text-red-400 font-medium mt-1">{worstDate.val} Failures</div>
                                </div>

                                {/* 3. Best Date (High Match) */}
                                <div className="bg-[#1a1d29] border border-gray-800 p-4 rounded-xl shadow-lg relative group overflow-hidden">
                                    <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                        <Calendar className="w-12 h-12 text-emerald-500" />
                                    </div>
                                    <div className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3 text-emerald-500" /> High Match Date
                                    </div>
                                    <div className="text-xl font-bold text-white truncate" title={bestDate.key}>{bestDate.key}</div>
                                    <div className="text-xs text-emerald-400 font-medium mt-1">{bestDate.val} Matches</div>
                                </div>

                                {/* 4. Worst Strategy */}
                                <div className="bg-[#1a1d29] border border-gray-800 p-4 rounded-xl shadow-lg relative group overflow-hidden">
                                    <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                        <Filter className="w-12 h-12 text-red-500" />
                                    </div>
                                    <div className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3 text-red-500" /> High Mismatch Strat
                                    </div>
                                    <div className="text-lg font-bold text-white truncate" title={worstStrat.key}>{worstStrat.key}</div>
                                    <div className="text-xs text-red-400 font-medium mt-1">{worstStrat.val} Failures</div>
                                </div>

                                {/* 5. Best Strategy */}
                                <div className="bg-[#1a1d29] border border-gray-800 p-4 rounded-xl shadow-lg relative group overflow-hidden">
                                    <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                        <Filter className="w-12 h-12 text-emerald-500" />
                                    </div>
                                    <div className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3 text-emerald-500" /> High Match Strat
                                    </div>
                                    <div className="text-lg font-bold text-white truncate" title={bestStrat.key}>{bestStrat.key}</div>
                                    <div className="text-xs text-emerald-400 font-medium mt-1">{bestStrat.val} Matches</div>
                                </div>
                            </>
                        );
                    })()}
                </div>
            )}

            {/* Results Table */}
            <div className={`flex flex-col bg-[#1a1d29] border border-gray-800 shadow-xl overflow-hidden transition-all duration-300
                ${isFullScreen ? 'fixed inset-0 z-50 m-0 rounded-none' : 'flex-1 rounded-xl'}`}>

                <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-[#1a1d29] shrink-0">
                    <div className="flex items-center gap-4">
                        <h3 className="text-sm font-bold text-gray-200 uppercase tracking-wider flex items-center gap-2">
                            <FileText className="w-4 h-4" /> Audit Results Log
                        </h3>
                        <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">{processedData.length} Record(s)</span>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setIsFullScreen(!isFullScreen)}
                            className={`p-2 rounded-lg text-gray-400 hover:text-white transition-all ${isFullScreen ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'}`}
                            title={isFullScreen ? "Exit Full Screen" : "Full Screen"}
                        >
                            {isFullScreen ? <X className="w-4 h-4" /> : <div className="border border-current w-4 h-3 rounded-sm" />}
                        </button>
                        <button onClick={fetchHistory} className="p-2 text-gray-400 hover:text-white bg-gray-800/50 hover:bg-gray-700 rounded-lg transition-all">
                            <RefreshCw className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto custom-scrollbar">
                    <table className="w-full text-left border-collapse min-w-[1500px]">
                        <thead className="bg-[#14161f] text-gray-500 sticky top-0 z-40 shadow-sm border-b border-[#1F2937]">
                            {/* Group Headers */}
                            <tr>
                                <th className="px-4 py-2 border-b border-[#1F2937] bg-[#14161f] w-[120px]"></th>

                                {/* Live Group Header */}
                                <th
                                    className={`px-4 py-2 shadow-[inset_0_-1px_0_0_#1F2937]
 border-[#1F2937] border-l border-r border-[#1F2937] text-center cursor-pointer transition-colors group
                                        ${collapsed.live ? 'bg-[#1a1d29]/80' : 'bg-[#1a1d29]/50 hover:bg-[#1a1d29]'}`}
                                    colSpan={collapsed.live ? 2 : 6}
                                    onClick={() => setCollapsed(prev => ({ ...prev, live: !prev.live }))}
                                >
                                    <div className="flex items-center justify-center gap-2 select-none text-blue-400">
                                        {collapsed.live ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                                        <span className="font-bold">Live Data</span>
                                    </div>
                                </th>

                                {/* Backtest Group Header */}
                                <th
                                    className={`px-4 py-2 shadow-[inset_0_-1px_0_0_#1F2937]
 border-[#1F2937] border-l border-r border-[#1F2937] text-center cursor-pointer transition-colors group
                                        ${collapsed.backtest ? 'bg-[#14161f]' : 'bg-[#14161f] hover:bg-gray-900'}`}
                                    colSpan={collapsed.backtest ? 2 : 6}
                                    onClick={() => setCollapsed(prev => ({ ...prev, backtest: !prev.backtest }))}
                                >
                                    <div className="flex items-center justify-center gap-2 select-none text-purple-400">
                                        {collapsed.backtest ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                                        <span className="font-bold">Backtest Data</span>
                                    </div>
                                </th>

                                <th className="px-4 py-2 shadow-[inset_0_-1px_0_0_#1F2937]
 border-[#1F2937] bg-[#14161f] w-[100px]"></th>
                                <th className="px-4 py-2 shadow-[inset_0_-1px_0_0_#1F2937]
 border-[#1F2937] bg-[#14161f] w-auto" colSpan={3}></th>
                            </tr>

                            {/* Column Headers with Filters */}
                            <tr>
                                <th className="px-4 py-3 shadow-[inset_0_-1px_0_0_#1F2937]
 border-[#1F2937] text-center w-[50px] text-xs font-bold uppercase text-gray-500">
                                    #
                                </th>

                                {/* Live Columns */}
                                {renderHeaderCell("Symbol", "symbol_l", "text", "bg-[#1a1d29]/30 border-l border-[#1F2937] min-w-[160px]")}
                                {renderHeaderCell("Strategy", "strategy_tag_l", "text", "bg-[#1a1d29]/30 min-w-[140px]")}
                                {!collapsed.live && (
                                    <>
                                        {renderHeaderCell("Buy Time", "buy_time_l", "time", "bg-[#1a1d29]/30 min-w-[140px]")}
                                        {renderHeaderCell("Buy Price", "buy_price_l", "price", "bg-[#1a1d29]/30 text-right min-w-[100px]")}
                                        {renderHeaderCell("Sell Time", "sell_time_l", "time", "bg-[#1a1d29]/30 min-w-[140px]")}
                                        {renderHeaderCell("Sell Price", "sell_price_l", "price", "bg-[#1a1d29]/30 text-right min-w-[100px]")}
                                    </>
                                )}

                                {/* Backtest Columns */}
                                {renderHeaderCell("Symbol", "symbol_b", "text", "bg-[#14161f] border-l border-[#1F2937] min-w-[160px]")}
                                {renderHeaderCell("Strategy", "strategy_tag_b", "text", "bg-[#14161f] min-w-[140px]")}
                                {!collapsed.backtest && (
                                    <>
                                        {renderHeaderCell("Buy Time", "buy_time_b", "time", "bg-[#14161f] min-w-[140px]")}
                                        {renderHeaderCell("Buy Price", "buy_price_b", "price", "bg-[#14161f] text-right min-w-[100px]")}
                                        {renderHeaderCell("Sell Time", "sell_time_b", "time", "bg-[#14161f] min-w-[140px]")}
                                        {renderHeaderCell("Sell Price", "sell_price_b", "price", "bg-[#14161f] text-right min-w-[100px]")}
                                    </>
                                )}

                                {renderHeaderCell("Status", "status", "status", "border-l border-[#1F2937] text-center w-[100px]")}
                                {renderHeaderCell("Comments", "comments", "text", "min-w-[200px]")}
                                {renderHeaderCell("Buy Slip %", "slippage_buy", "number", "text-right w-[90px]")}
                                {renderHeaderCell("Sell Slip %", "slippage_sell", "number", "text-right w-[90px]")}
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-800/50 text-xs text-gray-300">
                            {processedData.length === 0 ? (
                                <tr>
                                    <td colSpan="15" className="text-center py-12 text-gray-500 text-sm">No data matches your filters.</td>
                                </tr>
                            ) : (
                                processedData.map((row, index) => {
                                    const isMissingLive = !row.symbol_l && row.symbol_b;
                                    const isMissingBacktest = row.symbol_l && !row.symbol_b;
                                    const isMissingSide = isMissingLive || isMissingBacktest;
                                    const isMatch = row.status === 'Match';
                                    const isMismatch = row.status === 'Mismatch' && !isMissingSide;

                                    let rowClass = "hover:bg-[#202330] transition-colors group border-l-2 border-transparent";
                                    if (isMatch) rowClass = "bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-100 border-l-2 border-emerald-500";
                                    else if (isMissingSide) rowClass = "bg-red-500/10 hover:bg-red-500/20 text-red-100 border-l-2 border-red-500";
                                    else if (isMismatch) rowClass = "bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-100 border-l-2 border-yellow-500";

                                    return (
                                        <tr key={row.id} className={rowClass}>
                                            <td className={`px-4 py-2 font-mono text-center border-r border-[#1F2937]/50 text-xs ${isMatch ? 'text-emerald-200' : 'text-gray-500'}`}>
                                                {index + 1}
                                            </td>

                                            {/* Live Data */}
                                            <td className="px-4 py-2 font-medium border-r border-[#1F2937]/50">{row.symbol_l || '-'}</td>
                                            <td className="px-4 py-2 text-blue-300/80 border-r border-[#1F2937]/50 text-[11px]">{row.strategy_tag_l || '-'}</td>
                                            {!collapsed.live && (
                                                <>
                                                    <td className="px-4 py-2 text-gray-400 border-r border-[#1F2937]/50">{formatDateTime(row.buy_time_l)}</td>
                                                    <td className="px-4 py-2 font-mono text-right border-r border-[#1F2937]/50">{row.buy_price_l !== null ? Number(row.buy_price_l).toFixed(2) : '-'}</td>
                                                    <td className="px-4 py-2 text-gray-400 border-r border-[#1F2937]/50">{formatDateTime(row.sell_time_l)}</td>
                                                    <td className="px-4 py-2 font-mono text-right border-r border-gray-800/50">{row.sell_price_l !== null ? Number(row.sell_price_l).toFixed(2) : '-'}</td>
                                                </>
                                            )}

                                            {/* Backtest Data */}
                                            <td className="px-4 py-2 font-medium text-purple-200 border-r border-gray-800/50">{row.symbol_b || '-'}</td>
                                            <td className="px-4 py-2 text-purple-300/60 border-r border-gray-800/50 text-[11px]">{row.strategy_tag_b || '-'}</td>
                                            {!collapsed.backtest && (
                                                <>
                                                    <td className="px-4 py-2 text-gray-400 border-r border-gray-800/50">{formatDateTime(row.buy_time_b)}</td>
                                                    <td className="px-4 py-2 font-mono text-right border-r border-gray-800/50">{row.buy_price_b !== null ? Number(row.buy_price_b).toFixed(2) : '-'}</td>
                                                    <td className="px-4 py-2 text-gray-400 border-r border-gray-800/50">{formatDateTime(row.sell_time_b)}</td>
                                                    <td className="px-4 py-2 font-mono text-right border-r border-gray-800/50">{row.sell_price_b !== null ? Number(row.sell_price_b).toFixed(2) : '-'}</td>
                                                </>
                                            )}

                                            {/* Status */}
                                            <td className="px-4 py-2 text-center border-l border-r border-gray-800/50">
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border
                                                    ${row.status === 'Match' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                                        isMissingSide ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                                            'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'}`}>
                                                    {row.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 max-w-[200px] truncate border-r border-gray-800/50" title={row.comments}>
                                                {row.comments}
                                            </td>
                                            {/* Buy Slippage: Green if within 1%, Red otherwise */}
                                            <td className="px-4 py-2 text-right font-mono font-medium border-r border-gray-800/50">
                                                {row.slippage_buy !== null && row.slippage_buy !== undefined ? (
                                                    <span className={Math.abs(Number(row.slippage_buy)) <= 0.01 ? "text-emerald-400" : "text-red-400"}>
                                                        {(Number(row.slippage_buy) * 100).toFixed(2)}%
                                                    </span>
                                                ) : '-'}
                                            </td>
                                            {/* Sell Slippage: Green if within 1%, Red otherwise */}
                                            <td className="px-4 py-2 text-right font-mono font-medium">
                                                {row.slippage_sell !== null && row.slippage_sell !== undefined ? (
                                                    <span className={Math.abs(Number(row.slippage_sell)) <= 0.01 ? "text-emerald-400" : "text-red-400"}>
                                                        {(Number(row.slippage_sell) * 100).toFixed(2)}%
                                                    </span>
                                                ) : '-'}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const Audit = () => {
    const [activeTab, setActiveTab] = useState('backtest');

    return (
        <DashboardLayout>
            <div className="flex flex-col h-screen w-full bg-[#0f1116] text-gray-100 overflow-hidden">
                {/* Tabs Header */}
                <div className="flex border-b border-gray-800 bg-[#1a1d29] shrink-0">
                    <button
                        className={`px-6 py-4 text-sm font-medium transition-colors ${activeTab === 'backtest'
                            ? 'border-b-2 border-blue-500 text-blue-400'
                            : 'text-gray-400 hover:text-gray-200'
                            }`}
                        onClick={() => setActiveTab('backtest')}
                    >
                        Backtest
                    </button>
                    <button
                        className={`px-6 py-4 text-sm font-medium transition-colors ${activeTab === 'details'
                            ? 'border-b-2 border-blue-500 text-blue-400'
                            : 'text-gray-400 hover:text-gray-200'
                            }`}
                        onClick={() => setActiveTab('details')}
                    >
                        Audit
                    </button>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-hidden relative p-6">
                    {activeTab === 'backtest' ? (
                        <div className="h-full rounded-xl overflow-hidden border border-gray-800">
                            <BacktestView />
                        </div>
                    ) : (
                        <AuditWorkflow />
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default Audit;
